import { useTranslation } from 'react-i18next';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// One-time celebration shown the moment a worker accepts a job offer -- driven entirely by
// transient in-memory state set inside handleAcceptOffer (WorkerDashboard.jsx), never persisted
// anywhere. That's what makes "don't reappear on refresh" automatic: there's no dismissed-flag
// to manage, the state that triggers this modal simply doesn't exist anymore after a reload.
//
// Backdrop/card sizing mirrors the existing Accept Offer confirmation modal in
// WorkerDashboard.jsx (same dark NAVY_MID card, same 0.7-opacity backdrop, same shadow) --
// there's no shared generic Modal component in this codebase, so this matches that established
// inline convention rather than introducing a different modal shape.
export default function HireCelebrationModal({ companyName, jobTitle, hireDate, onViewDetails, onMessageEmployer, onClose }) {
  const { t, i18n } = useTranslation();
  const hireDateStr = hireDate
    ? new Date(hireDate).toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          backgroundColor: NAVY_MID, borderRadius: '14px',
          width: '100%', maxWidth: '440px',
          maxHeight: '90vh', overflowY: 'auto',
          padding: '34px 24px 28px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          textAlign: 'center',
        }}
      >
        <button
          onClick={onClose}
          aria-label={t('common.close')}
          style={{
            position: 'absolute', top: '14px', right: '14px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
            backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
            color: WHITE, fontSize: '16px', cursor: 'pointer', fontFamily: FONT_BODY,
          }}
        >
          ×
        </button>

        <div style={{
          width: '72px', height: '72px', borderRadius: '50%', backgroundColor: AMBER,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '34px', margin: '0 auto 18px',
        }}>
          🎉
        </div>

        <h2 style={{ fontFamily: FONT_HEAD, fontSize: '2rem', color: AMBER, letterSpacing: '0.03em', marginBottom: '12px' }}>
          {t('hireCelebration.heading')}
        </h2>

        <p style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', color: WHITE, lineHeight: 1.5, marginBottom: '6px' }}>
          {t('hireCelebration.hiredForRole', {
            company: companyName || t('hireCelebration.anEmployer'),
            job: jobTitle || t('hireCelebration.thisRole'),
          })}
        </p>

        {hireDateStr && (
          <p style={{ fontFamily: FONT_BODY, fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', marginBottom: '10px' }}>
            {t('hireCelebration.hiredOn', { date: hireDateStr })}
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px' }}>
          <button
            onClick={onViewDetails}
            style={{
              backgroundColor: AMBER, color: NAVY, fontWeight: 700, fontFamily: FONT_BODY,
              fontSize: '0.9rem', border: 'none', borderRadius: '8px', padding: '12px 20px', cursor: 'pointer',
            }}
          >
            {t('hireCelebration.viewDetails')}
          </button>
          <button
            onClick={onMessageEmployer}
            style={{
              backgroundColor: 'transparent', color: WHITE, fontWeight: 700, fontFamily: FONT_BODY,
              fontSize: '0.9rem', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', padding: '12px 20px', cursor: 'pointer',
            }}
          >
            {t('hireCelebration.messageEmployer')}
          </button>
        </div>
      </div>
    </div>
  );
}
